import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getSessionId } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext();

export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const updateCart = async (items) => {
    try {
      const response = await axios.put(`${API}/cart/${sessionId}`, {
        session_id: sessionId,
        items: items
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  const addToCart = async (item) => {
    const existingIndex = cart.items.findIndex(
      i => i.product_id === item.product_id && 
           JSON.stringify(i.config) === JSON.stringify(item.config)
    );

    let newItems;
    if (existingIndex >= 0) {
      newItems = [...cart.items];
      newItems[existingIndex].quantity += item.quantity;
      newItems[existingIndex].total_price = newItems[existingIndex].quantity * newItems[existingIndex].unit_price;
    } else {
      newItems = [...cart.items, item];
    }

    await updateCart(newItems);
    setIsCartOpen(true);
  };

  const removeFromCart = async (index) => {
    const newItems = cart.items.filter((_, i) => i !== index);
    await updateCart(newItems);
  };

  const updateQuantity = async (index, quantity) => {
    if (quantity < 1) return removeFromCart(index);
    
    const newItems = [...cart.items];
    newItems[index].quantity = quantity;
    newItems[index].total_price = quantity * newItems[index].unit_price;
    await updateCart(newItems);
  };

  const clearCart = async () => {
    await updateCart([]);
  };

  const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      cartCount,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      sessionId
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
